import { t } from "@lingui/macro";
import { createId } from "@paralleldrive/cuid2";
import { JsonResumeParser, ReactiveResumeParser } from "@stylingcv/parser";
import { useLocation, useNavigate } from "react-router-dom";

import { RESUMES_KEY } from "@/client/constants/query-keys";
import { useToast } from "@/client/hooks/use-toast";
import { queryClient } from "@/client/libs/query-client";
import { GuestSessionService } from "@/client/services/guest-session";
import { importResume } from "@/client/services/resume/import";
import { useAuthStore } from "@/client/stores/auth";

import { createLocalizedNavigation } from "../../../utils/url-locales";
import { useResumeLimit } from "../../../contexts/resume-limit-context";

// Helper function to get resume title from headline or fallback to default
const getResumeTitle = (resumeData: any, fallbackTitle: string): string => {
  const headline = resumeData?.basics?.headline?.trim();
  return headline && headline.length > 0 ? headline : fallbackTitle;
};

/**
 * Normalize a hybrid formatter output into proper Stylion format.
 * The production MCP formatter sometimes returns a mix of JSON Resume
 * and Stylion conventions (e.g. `sections.work` instead of `sections.experience`,
 * `basics.image` instead of `basics.picture`, location as object, IDs with underscores).
 * This function ensures the data matches the Stylion resumeDataSchema before validation.
 */
const normalizeFormatterOutput = (data: any): any => {
  if (!data || typeof data !== "object") return data;

  const normalized = { ...data };

  // --- Build basics from root when formatter returns flat structure ---
  if (!normalized.basics && (normalized.name != null || normalized.email != null || normalized.headline != null)) {
    normalized.basics = {
      name: typeof normalized.name === "string" ? normalized.name : "",
      headline: typeof normalized.headline === "string" ? normalized.headline : "",
      email: typeof normalized.email === "string" ? normalized.email : "",
      phone: typeof normalized.phone === "string" ? normalized.phone : "",
      location: typeof normalized.location === "string" ? normalized.location : "",
      url: normalized.url && typeof normalized.url === "object" ? { label: normalized.url.label ?? "", href: normalized.url.href ?? "" } : { label: "", href: "" },
      customFields: Array.isArray(normalized.customFields) ? normalized.customFields : [],
      picture: normalized.picture && typeof normalized.picture === "object" ? normalized.picture : {
        url: "", size: 120, aspectRatio: 1, borderRadius: 1,
        effects: { hidden: false, border: true, grayscale: false },
      },
    };
  }

  // --- Normalize basics ---
  if (normalized.basics) {
    const b = { ...normalized.basics };

    // basics.label → basics.headline
    if (!b.headline && b.label) {
      b.headline = b.label;
      delete b.label;
    }
    if (!b.headline) b.headline = "";

    // basics.image → basics.picture
    if (!b.picture && b.image) {
      b.picture = b.image;
      delete b.image;
    }
    if (!b.picture) {
      b.picture = {
        url: "",
        size: 64,
        aspectRatio: 1,
        borderRadius: 0,
        effects: { hidden: false, border: false, grayscale: false },
      };
    }

    // basics.location must be a string
    if (b.location && typeof b.location === "object") {
      const loc = b.location as Record<string, string>;
      b.location = [loc.address, loc.city, loc.region, loc.postalCode, loc.countryCode]
        .filter(Boolean)
        .join(", ") || "";
    }
    if (typeof b.location !== "string") b.location = "";

    // Ensure required string fields
    if (typeof b.name !== "string") b.name = "";
    if (typeof b.phone !== "string") b.phone = "";
    // email schema: z.literal("").or(z.string().email()).optional()
    // If the AI gave us junk, delete the key so .optional() passes cleanly
    if (typeof b.email !== "string" || (b.email !== "" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(b.email))) {
      delete b.email;
    }
    if (!b.url) b.url = { label: "", href: "" };
    if (!Array.isArray(b.customFields)) b.customFields = [];

    // Fix custom field IDs and ensure required fields exist
    // AI sometimes returns strings instead of objects (e.g. ["Nationality: Jordanian"])
    b.customFields = b.customFields
      .map((cf: any) => {
        // Handle string entries: convert "Key: Value" to proper object
        if (typeof cf === "string") {
          const parts = cf.split(/:\s*/);
          return {
            id: createId(),
            icon: "",
            name: parts[0] || cf,
            value: parts.slice(1).join(": ") || "",
            visible: true,
          };
        }
        if (!cf || typeof cf !== "object") return null;
        return {
          id: createId(),
          icon: typeof cf.icon === "string" ? cf.icon : "",
          name: typeof cf.name === "string" ? cf.name : "",
          value: typeof cf.value === "string" ? cf.value : "",
          visible: cf.visible !== false,
        };
      })
      .filter(Boolean);

    // Remove profiles from basics (belongs in sections.profiles)
    const basicsProfiles = b.profiles;
    delete b.profiles;
    // Remove summary from basics (belongs in sections.summary)
    const basicsSummary = b.summary;
    delete b.summary;
    // Remove label if still present
    delete b.label;
    delete b.image;

    normalized.basics = b;

    // Store extracted fields for sections normalization
    normalized._extractedProfiles = basicsProfiles;
    normalized._extractedSummary = basicsSummary;
  }

  // --- Normalize sections ---
  if (normalized.sections) {
    const s = { ...normalized.sections };

    // sections.work → sections.experience
    if (s.work && !s.experience) {
      s.experience = {
        ...s.work,
        id: "experience",
        name: s.work.name || "Experience",
      };
      delete s.work;
    }

    // Ensure summary section exists and always has the correct literal id
    if (!s.summary) {
      s.summary = {
        id: "summary",
        name: "Summary",
        columns: 1,
        separateLinks: true,
        visible: true,
        content: normalized._extractedSummary || "",
      };
    } else {
      // AI may return a random id — schema requires literal "summary"
      s.summary = {
        columns: 1,
        separateLinks: true,
        visible: true,
        ...s.summary,
        id: "summary",
        name: s.summary.name || "Summary",
      };
    }
    // Strip HTML tags helper for summary content check
    if (!s.summary.content && normalized._extractedSummary) {
      s.summary.content = normalized._extractedSummary;
    }

    // Ensure all required sections exist with defaults
    const requiredSections: Record<string, any> = {
      awards: { id: "awards", name: "Awards", items: [] },
      certifications: { id: "certifications", name: "Certifications", items: [] },
      education: { id: "education", name: "Education", items: [] },
      experience: { id: "experience", name: "Experience", items: [] },
      volunteer: { id: "volunteer", name: "Volunteering", items: [] },
      interests: { id: "interests", name: "Interests", items: [] },
      languages: { id: "languages", name: "Languages", items: [] },
      profiles: { id: "profiles", name: "Profiles", items: [] },
      projects: { id: "projects", name: "Projects", items: [] },
      publications: { id: "publications", name: "Publications", items: [] },
      hobbies: { id: "hobbies", name: "Hobbies", items: [] },
      references: { id: "references", name: "References", items: [] },
      signature: { id: "signature", name: "Signature", items: [] },
      skills: { id: "skills", name: "Skills", items: [] },
    };

    for (const [key, defaults] of Object.entries(requiredSections)) {
      if (!s[key]) {
        s[key] = { columns: 1, separateLinks: true, visible: true, ...defaults };
      } else {
        // Ensure section has correct id and required fields
        s[key] = {
          columns: 1,
          separateLinks: true,
          visible: true,
          ...s[key],
          id: defaults.id,
          name: s[key].name || defaults.name,
        };
        // Ensure items array exists for item-based sections
        if ("items" in defaults && !Array.isArray(s[key].items)) {
          s[key].items = [];
        }
      }
    }

    // Move basics.profiles to sections.profiles if it was populated
    if (normalized._extractedProfiles && Array.isArray(normalized._extractedProfiles) && normalized._extractedProfiles.length > 0) {
      s.profiles.items = normalized._extractedProfiles.map((p: any) => ({
        id: createId(),
        visible: true,
        network: p.network || "",
        username: p.username || "",
        icon: p.network?.toLowerCase() || "",
        url: typeof p.url === "string" ? { label: "", href: p.url } : (p.url || { label: "", href: "" }),
      }));
    }

    // Sections whose items have a `url` field that must be { label: string, href: string }
    const sectionsWithUrl = new Set([
      "experience", "education", "certifications", "awards", "projects",
      "publications", "volunteer", "references", "profiles", "custom",
    ]);

    // Fix all item IDs and URL fields across all sections
    for (const key of Object.keys(s)) {
      if (key === "custom" || key === "summary") continue;
      const section = s[key];
      if (section?.items && Array.isArray(section.items)) {
        section.items = section.items.map((item: any) => {
          const fixed: any = {
            ...item,
            id: createId(),
            visible: item.visible !== false, // default to true
          };
          // Ensure required string fields per section type
          if (key === "experience") {
            if (typeof fixed.location !== "string") fixed.location = "";
            if (typeof fixed.position !== "string") fixed.position = "";
            if (typeof fixed.date !== "string") fixed.date = "";
            if (typeof fixed.summary !== "string") fixed.summary = "";
          }
          if (key === "education") {
            if (typeof fixed.score !== "string") fixed.score = "";
            if (typeof fixed.studyType !== "string") fixed.studyType = "";
            if (typeof fixed.area !== "string") fixed.area = "";
            if (typeof fixed.date !== "string") fixed.date = "";
            if (typeof fixed.summary !== "string") fixed.summary = "";
          }
          if (key === "skills") {
            if (typeof fixed.description !== "string") fixed.description = "";
            if (typeof fixed.name !== "string") fixed.name = "";
          }
          if (key === "references") {
            if (typeof fixed.description !== "string") fixed.description = "";
            if (typeof fixed.summary !== "string") fixed.summary = "";
          }
          if (key === "certifications") {
            if (typeof fixed.issuer !== "string") fixed.issuer = "";
            if (typeof fixed.date !== "string") fixed.date = "";
            if (typeof fixed.summary !== "string") fixed.summary = "";
          }
          if (key === "awards") {
            if (typeof fixed.awarder !== "string") fixed.awarder = "";
            if (typeof fixed.date !== "string") fixed.date = "";
            if (typeof fixed.summary !== "string") fixed.summary = "";
          }
          if (key === "projects") {
            if (typeof fixed.description !== "string") fixed.description = "";
            if (typeof fixed.date !== "string") fixed.date = "";
            if (typeof fixed.summary !== "string") fixed.summary = "";
          }
          if (key === "volunteer") {
            if (typeof fixed.organization !== "string") fixed.organization = "";
            if (typeof fixed.position !== "string") fixed.position = "";
            if (typeof fixed.location !== "string") fixed.location = "";
            if (typeof fixed.date !== "string") fixed.date = "";
            if (typeof fixed.summary !== "string") fixed.summary = "";
          }
          if (key === "publications") {
            if (typeof fixed.publisher !== "string") fixed.publisher = "";
            if (typeof fixed.date !== "string") fixed.date = "";
            if (typeof fixed.summary !== "string") fixed.summary = "";
          }
          // Normalize url field: string → { label: "", href: string } or ensure object shape
          if (sectionsWithUrl.has(key)) {
            if (typeof fixed.url === "string") {
              fixed.url = { label: "", href: fixed.url || "" };
            } else if (!fixed.url || typeof fixed.url !== "object") {
              fixed.url = { label: "", href: "" };
            } else {
              // Ensure both label and href exist
              fixed.url = {
                label: fixed.url.label || "",
                href: fixed.url.href || "",
              };
            }
          }
          return fixed;
        });
      }
    }

    // Ensure custom is empty object
    if (!s.custom || typeof s.custom !== "object" || Array.isArray(s.custom)) {
      s.custom = {};
    }

    normalized.sections = s;
  }

  // --- Normalize metadata ---
  if (!normalized.metadata) {
    normalized.metadata = {};
  }
  const m = { ...normalized.metadata };
  if (!m.css || typeof m.css !== "object") {
    m.css = { value: "", visible: false };
  }
  if (!m.page || typeof m.page !== "object") {
    m.page = { margin: 40, format: "a4", options: { breakLine: false, pageNumbers: true } };
  } else {
    if (!m.page.options || typeof m.page.options !== "object") {
      m.page.options = { breakLine: false, pageNumbers: true };
    }
  }
  if (!m.theme || typeof m.theme !== "object") {
    m.theme = { background: "#ffffff", text: "#000000", primary: "#1b79db", isFree: "NO" };
  }
  if (!m.typography || typeof m.typography !== "object") {
    m.typography = {
      font: { family: "IBM Plex Serif", subset: "latin", variants: ["regular"], size: 14.5 },
      lineHeight: 1.75, hideIcons: false, underlineLinks: true, headerFontSize: 22,
    };
  } else if (!m.typography.font || typeof m.typography.font !== "object") {
    m.typography.font = { family: "IBM Plex Serif", subset: "latin", variants: ["regular"], size: 14.5 };
  }
  if (typeof m.notes !== "string") m.notes = "";
  if (typeof m.signature !== "string") m.signature = "";
  if (typeof m.locale !== "string") m.locale = "en-US";
  if (typeof m.template !== "string") m.template = "leafish";
  if (typeof m.schemaVersion !== "string") m.schemaVersion = "1.0.0";
  if (typeof m.backgroundImage !== "string") m.backgroundImage = "";
  normalized.metadata = m;

  // Clean up temporary fields
  delete normalized._extractedProfiles;
  delete normalized._extractedSummary;

  return normalized;
};

/** Check if a string is a valid CUID2 (lowercase alphanumeric only) */
const isValidCuid2 = (id: any): boolean => {
  return typeof id === "string" && id.length > 0 && /^[0-9a-z]+$/.test(id);
};

// Language code to locale mapping
const LANGUAGE_TO_LOCALE_MAP: Record<string, string> = {
  EN: "en-US",
  AR: "ar-SA",
  FR: "fr-FR",
  DE: "de-DE",
  ES: "es-ES",
  IT: "it-IT",
  PT: "pt-PT",
  NL: "nl-NL",
  RU: "ru-RU",
  ZH: "zh-CN",
  JA: "ja-JP",
  KO: "ko-KR",
};

export const useResumeTransfer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getLocalizedPath } = createLocalizedNavigation(location.pathname);
  const { toast } = useToast();
  const { handleLimitReached } = useResumeLimit();
  const isLoggedIn = useAuthStore((state: any) => !!state.user);
  const user = useAuthStore((state: any) => state.user);

  const transferToBuilder = async (rawAiJson: any, marketContext?: any, userLanguage?: string) => {
    try {
      if (!rawAiJson) {
        throw new Error("No AI data provided");
      }

      let validatedData;
      try {
        const parsedAiData = typeof rawAiJson === "string" ? JSON.parse(rawAiJson) : rawAiJson;

        // CRITICAL: Normalize hybrid formatter output before any validation.
        // Production MCP formatter may return data with JSON Resume conventions
        // inside a Stylion-like envelope (e.g. sections.work instead of sections.experience,
        // basics.image instead of basics.picture, location as object, IDs with underscores).
        // This normalization must happen BEFORE format detection and validation.
        const normalizedData = normalizeFormatterOutput(parsedAiData);
        // Replace parsedAiData reference with normalized version for all downstream code
        Object.assign(parsedAiData, normalizedData);

        // Defensive normalization: some AI outputs set sections.custom incorrectly.
        // The schema expects an empty object {}, so fix any invalid values.
        try {
          const sections: any = parsedAiData?.sections;
          if (
            sections && // Fix custom section - must be empty object
            (!sections.custom ||
              typeof sections.custom !== "object" ||
              sections.custom === null ||
              Array.isArray(sections.custom) ||
              Object.keys(sections.custom).length > 0)
          ) {
            sections.custom = {};
          }
        } catch {
          // If anything goes wrong, do not block validation; the parser will handle remaining issues.
        }

        // Sanitize empty required strings in section items so Zod .min(1) checks pass.
        try {
          const sections: any = parsedAiData?.sections;
          if (sections) {
            const requiredFields: Record<string, string[]> = {
              experience: ["company"],
              education: ["institution"],
              certifications: ["name"],
              awards: ["title"],
              projects: ["name"],
              volunteer: ["organization"],
              languages: ["name"],
              hobbies: ["name"],
              interests: ["name"],
              references: ["name"],
              publications: ["name"],
            };
            const fallbackLabels: Record<string, Record<string, string>> = {
              experience: { company: "(Untitled Company)" },
              education: { institution: "(Untitled Institution)" },
              certifications: { name: "(Untitled)" },
              awards: { title: "(Untitled)" },
              projects: { name: "(Untitled Project)" },
              volunteer: { organization: "(Untitled Organization)" },
              languages: { name: "(Untitled)" },
              hobbies: { name: "(Untitled)" },
              interests: { name: "(Untitled)" },
              references: { name: "(Untitled)" },
              publications: { name: "(Untitled)" },
            };
            for (const [sectionKey, fields] of Object.entries(requiredFields)) {
              const items = sections[sectionKey]?.items;
              if (Array.isArray(items)) {
                for (const item of items) {
                  for (const field of fields) {
                    if (typeof item[field] !== "string" || item[field].trim() === "") {
                      item[field] = fallbackLabels[sectionKey]?.[field] ?? "(Untitled)";
                    }
                  }
                }
              }
            }
          }
        } catch {
          // Non-blocking: parser will catch remaining issues
        }

        // Inject user email before validation so Zod sees a valid value
        if (user?.email && parsedAiData?.basics) {
          parsedAiData.basics.email = user.email;
        }

        // Detect if AI returned JSON Resume format (has "work" key) vs Stylion format (has "sections" key)
        const isJsonResumeFormat =
          ("work" in parsedAiData || "certificates" in parsedAiData) && !("sections" in parsedAiData);

        if (isJsonResumeFormat) {
          console.log("🔄 Detected JSON Resume format from AI, converting to Stylion format");
          const jsonParser = new JsonResumeParser();
          const jsonValidated = jsonParser.validate(parsedAiData);
          validatedData = jsonParser.convert(jsonValidated);
        } else {
          // Already in Stylion format
          const parser = new ReactiveResumeParser();
          const validationResult = parser.validate(parsedAiData);
          validatedData = parser.convert(validationResult);
        }

        // CRITICAL: Ensure exact metadata settings from generated resume
        if (validatedData?.metadata) {
          // Convert language code to proper locale if needed
          const currentLocale = validatedData.metadata.locale;
          let finalLocale = currentLocale;

          // PRIORITY 1: Use the user's explicit language selection from the form
          const normalizedUserLang = userLanguage?.toUpperCase();
          if (normalizedUserLang && LANGUAGE_TO_LOCALE_MAP[normalizedUserLang]) {
            finalLocale = LANGUAGE_TO_LOCALE_MAP[normalizedUserLang];
            console.log(
              "🔄 Using user's explicit language selection:",
              userLanguage,
              "->",
              finalLocale,
            );
          }
          // PRIORITY 2: Check if AI provided a language code instead of locale
          else if (currentLocale && LANGUAGE_TO_LOCALE_MAP[currentLocale]) {
            finalLocale = LANGUAGE_TO_LOCALE_MAP[currentLocale];
            console.log(
              "🔄 Converting AI language code to locale:",
              currentLocale,
              "->",
              finalLocale,
            );
          }
          // PRIORITY 3: Fallback — detect Arabic content by Unicode in name
          else if (currentLocale === "en-US") {
            const hasArabicText =
              parsedAiData?.basics?.name && /[\u0600-\u06FF]/.test(parsedAiData.basics.name);
            if (hasArabicText) {
              finalLocale = "ar-SA";
              console.log("🔄 Detected Arabic content, setting locale to ar-SA");
            }
          }

          // Apply Arabic-specific metadata if language is Arabic
          if (finalLocale === "ar-SA") {
            console.log("🇸🇦 Applying Arabic metadata in transfer");
            validatedData.metadata = {
              schemaVersion: "1.0.0",
              css: {
                value: "",
                visible: false,
              },
              page: {
                format: "a4",
                margin: 42,
                options: {
                  breakLine: false,
                  pageNumbers: true,
                },
              },
              notes: "",
              theme: {
                text: "#000000",
                isFree: "NO",
                primary: "#1b79db",
                background: "#ffffff",
              },
              layout: [
                [
                  ["summary", "experience", "education", "projects", "certifications", "awards", "mostProudOf", "myMind"],
                  [
                    "skills",
                    "languages",
                    "profiles",
                    "interests",
                    "hobbies",
                    "volunteer",
                    "publications",
                    "references",
                    "signature",
                  ],
                ],
              ],
              locale: "ar-SA",
              template: "vibrant",
              signature: "Your Name",
              typography: {
                font: {
                  size: 16.5,
                  family: "Tajawal",
                  subset: "arabic",
                  variants: ["regular"],
                },
                bodyFont: {
                  family: "IBM Plex Sans Arabic",
                  subset: "arabic",
                  variants: ["regular"],
                },
                hideIcons: true,
                lineHeight: 1.75,
                headerFontSize: 24,
                underlineLinks: true,
              },
              backgroundImage: "bg7",
              marketContext: marketContext || undefined,
            };
          } else {
            validatedData.metadata = {
              ...validatedData.metadata,
              schemaVersion: "1.0.0",
              locale: finalLocale || "en-US", // Use converted locale or default
              template: "vibrant",
              layout: [
                [
                  ["summary", "experience", "education", "projects", "certifications", "awards", "volunteer", "publications", "mostProudOf", "myMind"],
                  [
                    "skills",
                    "languages",
                    "profiles",
                    "interests",
                    "hobbies",
                    "references",
                    "signature",
                  ],
                ],
              ],
              css: {
                value: "",
                visible: false,
              },
              page: {
                margin: 42,
                format: "a4",
                options: {
                  breakLine: false,
                  pageNumbers: true,
                },
              },
              theme: {
                background: "#ffffff",
                text: "#000000",
                primary: "#0284c7",
                isFree: "NO",
              },
              typography: {
                font: {
                  family: "Playfair Display",
                  subset: "latin",
                  variants: ["regular"],
                  size: 16.5,
                },
                bodyFont: {
                  family: "Miriam Libre",
                  subset: "latin",
                  variants: ["regular"],
                },
                lineHeight: 1.75,
                hideIcons: true,
                underlineLinks: true,
                headerFontSize: 24,
              },
              notes: "",
              backgroundImage: "bg7",
              signature: "",
              marketContext: marketContext || undefined,
            };
          }
          console.log("🎨 Applied exact generated resume settings in transfer");
        }

        // CRITICAL: Use user's profile picture if logged in, otherwise use default
        if (validatedData?.basics?.picture) {
          const currentPictureUrl = validatedData.basics.picture.url;

          if (isLoggedIn && user?.picture) {
            // For logged-in users, use their profile picture
            validatedData.basics.picture.url = user.picture;
            console.log("🖼️ Using user's profile picture:", user.picture);
          } else if (
            !currentPictureUrl ||
            currentPictureUrl.includes("digitaloceanspaces.com") ||
            currentPictureUrl.includes("http")
          ) {
            // For guests or users without pictures, use default
            validatedData.basics.picture.url = "/artboard/images/default-profile.jpg";
            console.log(
              "🖼️ Set default profile picture in transfer:",
              validatedData.basics.picture.url,
            );
          }
        }

        console.log("✅ AI data validated and converted successfully:", validatedData);

        // Signature override: ensure user's actual name is always used
        // (Hobbies, references, and languages are handled by the formatter as source of truth)
        try {
          const sections: any = validatedData.sections;
          const basics: any = validatedData.basics;
          const metadata: any = validatedData.metadata;

          if (sections && basics) {
            const isArabic = metadata?.locale?.toLowerCase().startsWith("ar");
            const signatureContent = isArabic ? "Your Name" : basics.name || "Your Name";
            sections.signature = {
              id: "signature",
              name: "Signature",
              columns: 1,
              separateLinks: true,
              visible: true,
              items: [
                {
                  id: createId(),
                  visible: true,
                  content: signatureContent,
                  image: "",
                  date: "",
                },
              ],
            };

            console.log("✅ Applied signature override with user's name");
          }
        } catch (error) {
          console.warn("⚠️ Could not apply signature override:", error);
        }

        // Preserve parser-assigned unique CUID2 IDs. Do not strip them.
      } catch (validationError: any) {
        // Log structured Zod errors for debugging
        if (validationError?.issues) {
          console.error(
            "[Resume Validation] Zod errors:",
            validationError.issues.map((i: any) => ({
              path: i.path.join("."),
              message: i.message,
              code: i.code,
            })),
          );
        } else {
          console.error("❌ Failed to validate AI data:", validationError);
        }

        // Show error to user
        toast({
          title: t`Validation Error`,
          description: t`The AI-generated resume data could not be validated. Please try generating again.`,
          variant: "error",
        });
        return null;
      }

      // Check if user is authenticated
      if (isLoggedIn) {
        // User is authenticated, proceed with resume import
        // Use EXACT same call as manual import dialog

        const importPayload = {
          data: validatedData,
          title: getResumeTitle(validatedData, t`AI Generated Resume`),
          slug: `ai-generated-resume-${Date.now()}`,
        };

        try {
          const createdResume = await importResume(importPayload);

          // Invalidate resumes query to ensure dashboard shows the new resume
          await queryClient.invalidateQueries({ queryKey: RESUMES_KEY });

          toast({
            title: t`Resume Created Successfully`,
            description: t`Your AI-generated resume has been imported. You can now edit it in the builder.`,
            variant: "default",
          });

          // Navigate to the builder with the new resume
          navigate(getLocalizedPath(`/builder/${createdResume.id}`), { replace: true });

          // 🏆 TRIGGER ACHIEVEMENT: First resume created
          // This will be automatically tracked by useAchievementTracker when resumes list updates
          return createdResume;
        } catch (error) {
          console.error("❌ Import failed:", error);

          // Check if this is a resume limit error
          if (handleLimitReached(error)) {
            return {
              error: "RESUME_LIMIT_REACHED",
              message: error instanceof Error ? error.message : String(error),
              needsUpgrade: true,
            };
          }

          toast({
            title: t`Import Failed`,
            description: t`Failed to import your resume. Please try again.`,
            variant: "error",
          });
          return null;
        }
      } else {
        // Create the same importPayload format as authenticated users
        const importPayload = {
          data: validatedData,
          title: getResumeTitle(validatedData, t`AI Generated Resume`),
          slug: `ai-generated-resume-${Date.now()}`,
        };

        try {
          // IMPORTANT: Ensure guest session exists before importing
          // This creates the session cookie if it doesn't exist yet
          await GuestSessionService.initializeSession();
          console.log("✅ Guest session initialized before import");

          // Use the new guest import API (same flow as authenticated users!)
          const guestResume = await GuestSessionService.importResume(importPayload);

          // Check if response is an error object instead of resume
          if ((guestResume as any).success === false) {
            throw new Error((guestResume as any).message || "Guest import failed");
          }

          // Validate that we have a proper resume with ID
          if (!guestResume?.id) {
            console.error("❌ Guest resume missing ID:", guestResume);
            throw new Error("Guest resume was created but ID is missing");
          }

          console.log("✅ Guest import successful, created resume with ID:", guestResume.id);

          // NOTE: Do NOT invalidateQueries for RESUMES_KEY here.
          // Guests have no auth tokens, so refetching /api/resume triggers a 401 cascade
          // that crashes the app (React Error #185). The cache will refresh when the user logs in.

          toast({
            title: t`Resume Created Successfully`,
            description: t`Your AI-generated resume has been created. You can continue editing in the builder.`,
            variant: "default",
          });

          // Navigate to guest builder with the validated resume ID
          navigate(getLocalizedPath(`/builder/guest/${guestResume.id}`), { replace: true });
          return guestResume;
        } catch (error) {
          console.error("❌ Guest import failed:", error);

          // Check if this is a resume limit error for guests
          if (handleLimitReached(error)) {
            return {
              error: "RESUME_LIMIT_REACHED",
              message: error instanceof Error ? error.message : String(error),
              needsUpgrade: true,
            };
          }

          toast({
            title: t`Import Failed`,
            description: t`Failed to save your resume. Please try again.`,
            variant: "error",
          });
          return null;
        }
      }
    } catch (error) {
      console.error("❌ Transfer to builder failed:", error);
      toast({
        title: t`Transfer Failed`,
        description: t`Something went wrong while transferring your resume. Please try again.`,
        variant: "error",
      });
      return null;
    }
  };

  return {
    transferToBuilder,
  };
};
